import type { MaterialsRow } from "@workspace/api-zod/materials-list";
import { getCategorySection, getItemSubGroup, ITEM_SUB_GROUP_LABELS, STAGE_SUB_GROUP_ORDER } from "@workspace/api-zod/work-type";

const stages = Object.entries(STAGE_SUB_GROUP_ORDER);

/** Display-only grouping: never splits or recomputes an aggregated material row. */
export function materialsCategoryGroups<T extends Pick<MaterialsRow, "category" | "contributors" | "name"> & { spec?: string }>(rows: T[]) {
  const groups = new Map<string, { rows: T[]; order: number; section: string }>();
  for (const row of rows) {
    const subgroup = getItemSubGroup(row.category, row.contributors[0]?.name);
    // Summary-quote sections fold Substructure/Roofing into Structure; use the
    // work-item stage for the purchasing label/icon when a subgroup exists.
    const section = subgroup
      ? stages.find(([, ids]) => ids.includes(subgroup))?.[0] ?? getCategorySection(row.category)
      : getCategorySection(row.category);
    const category = subgroup ? ITEM_SUB_GROUP_LABELS[subgroup] : section;
    const order = subgroup
      ? (stages.findIndex(([, ids]) => ids.includes(subgroup)) + 1) * 100
        + (stages.find(([, ids]) => ids.includes(subgroup))?.[1].indexOf(subgroup) ?? 0)
      : section === "Preparation" ? 0
        : (stages.findIndex(([stage]) => stage === section) + 1 || stages.length + 1) * 100 + 99;
    const group = groups.get(category) ?? { rows: [], order, section };
    group.rows.push(row);
    groups.set(category, group);
  }
  return [...groups].sort(([, a], [, b]) => a.order - b.order)
    .map(([category, group]) => ({
      category,
      section: group.section,
      rows: [...group.rows].sort((a, b) => a.name.localeCompare(b.name, "en-GB", { numeric: true })
        || (a.spec ?? "").localeCompare(b.spec ?? "", "en-GB", { numeric: true })),
    }));
}
